import { useRef, useCallback, useEffect } from "react";

export const PolygonLassoOverlay = ({ widthPx, heightPx, transform, className = "", imgRef, points, setPoints }) => {

  const svgRef = useRef(null);
  const dragIdxRef = useRef(null);          // index of the point being dragged
  const wasDraggingRef = useRef(false);    // true only if pointer actually moved
  const dragStartRef = useRef({ x: 0, y: 0 });
  const DRAG_THRESHOLD_PX = 5;

  const clamp = (v, min, max) => Math.max(min, Math.min(max, v));

  /* Helpers */

  const eventToImagePoint = useCallback((e) => {
    const imgEl = imgRef?.current;
    if (!imgEl || !widthPx || !heightPx) return null;

    const rect = imgEl.getBoundingClientRect();

    const rx = (e.clientX - rect.left) / rect.width;
    const ry = (e.clientY - rect.top) / rect.height;

    const x = clamp(ry * widthPx, 0, widthPx);
    const y = clamp(heightPx - rx * heightPx, 0, heightPx);

    return { x, y };
  }, [imgRef, widthPx, heightPx]);

  const findHitPointIndex = useCallback((pt, hitRadiusPx = 20) => {
    const r2 = hitRadiusPx * hitRadiusPx;
    for (let i = 0; i < points.length; i++) {
      const dx = points[i].x - pt.x;
      const dy = points[i].y - pt.y;
      if (dx * dx + dy * dy <= r2) return i;
    }
    return -1;
  }, [points]);

  /* Window events */

  const onWindowPointerMove = useCallback((e) => {
    const idx = dragIdxRef.current;
    if (idx === null) return;

    if (e.cancelable) e.preventDefault();

    const p = eventToImagePoint(e);
    if (!p) return;

    const dx = p.x - dragStartRef.current.x;
    const dy = p.y - dragStartRef.current.y;

    // Confirm drag only after a minimal movement
    if (!wasDraggingRef.current &&
      (dx * dx + dy * dy) >= DRAG_THRESHOLD_PX * DRAG_THRESHOLD_PX) {
      wasDraggingRef.current = true;
    }

    if (!wasDraggingRef.current) return;

    // Move the dragged point
    setPoints((prev) => {
      const next = prev.slice();
      next[idx] = p;
      return next;
    });
  }, [eventToImagePoint, setPoints]);

  const onWindowPointerUp = useCallback(() => {
    dragIdxRef.current = null;
    detachWindowDragListeners();

    setTimeout(() => { wasDraggingRef.current = false; }, 0);
  }, [eventToImagePoint, findHitPointIndex, setPoints]);

  const attachWindowDragListeners = useCallback(() => {
    window.addEventListener("pointermove", onWindowPointerMove, { passive: false });
    window.addEventListener("pointerup", onWindowPointerUp, { passive: false });
    window.addEventListener("pointercancel", onWindowPointerUp, { passive: false });
  }, [onWindowPointerMove, onWindowPointerUp]);

  const detachWindowDragListeners = useCallback(() => {
    window.removeEventListener("pointermove", onWindowPointerMove);
    window.removeEventListener("pointerup", onWindowPointerUp);
    window.removeEventListener("pointercancel", onWindowPointerUp);
  }, [onWindowPointerMove, onWindowPointerUp]);

  /* Image events */

  const onImgPointerDown = useCallback((e) => {
    e.stopPropagation();
    if (e.cancelable) e.preventDefault();

    const p = eventToImagePoint(e);
    if (!p) return;

    const hitIdx = findHitPointIndex(p, 40);

    if (hitIdx >= 0) {
      // Start a potential drag on this point
      dragIdxRef.current = hitIdx;
      wasDraggingRef.current = false;
      dragStartRef.current = { x: p.x, y: p.y };

      attachWindowDragListeners(); // IMPORTANT: start receiving pointermove even if pointer leaves the image
      return;
    }

    // Tap on background -> add a new point
    setPoints((prev) => [...prev, p]);
  }, [eventToImagePoint, findHitPointIndex, setPoints]);

  const onImgPointerUp = useCallback((e) => {
    const idx = dragIdxRef.current;

    if (idx !== null && !wasDraggingRef.current) {
      e.stopPropagation();
      if (e.cancelable) e.preventDefault();
      setPoints((prev) => prev.filter((_, i) => i !== idx));
    }

    onWindowPointerUp(); // IMPORTANT: cleanup even if pointerup happens on the image
  }, [setPoints, onWindowPointerUp]);

  useEffect(() => {
    const imgEl = imgRef?.current;
    if (!imgEl) return;

    imgEl.style.touchAction = "none";

    imgEl.addEventListener("pointerdown", onImgPointerDown, { passive: false });
    imgEl.addEventListener("pointerup", onImgPointerUp, { passive: false });
    imgEl.addEventListener("pointercancel", onWindowPointerUp, { passive: false });

    return () => {
      imgEl.removeEventListener("pointerdown", onImgPointerDown);
      imgEl.removeEventListener("pointerup", onImgPointerUp);
      imgEl.removeEventListener("pointercancel", onWindowPointerUp);
    };
  }, [imgRef, onImgPointerDown, onImgPointerUp, onWindowPointerUp]);

  if (!widthPx || !heightPx) return null;

  return (
    <svg
      ref={svgRef}
      className={`absolute block select-none left-1/2 top-1/2 ${className}`}
      style={{
        width: `${widthPx}px`,
        height: `${heightPx}px`,
        transform: transform,
        pointerEvents: 'none',
      }}
      viewBox={`0 0 ${widthPx} ${heightPx}`}
    >
      {/* Filled polygon + outline */}
      {points.length >= 3 && (
        <polygon
          points={points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="rgba(65, 100, 253, 0.35)"
          stroke="rgb(65, 100, 253)"
          strokeWidth="4"
        />
      )}

      {/* Outline preview when only 2 points */}
      {points.length === 2 && (
        <polyline
          points={points.map((p) => `${p.x},${p.y}`).join(" ")}
          fill="none"
          stroke="rgb(65, 100, 253)"
          strokeWidth="4"
        />
      )}

      {/* Point handles */}
      {points.map((p, idx) => (
        <g key={idx}>
          <circle
            cx={p.x}
            cy={p.y}
            r="14"
            fill="rgb(255, 255, 255)"
            stroke="rgb(65, 100, 253)"
            strokeWidth="4"
          />
          <circle
            cx={p.x}
            cy={p.y}
            r="4"
            fill="rgb(65, 100, 253)"
          />
        </g>
      ))}
    </svg>
  );
};
